import { useStacklineTheme } from '@stackline/ui';
import React, { useMemo, useState } from 'react';
import ReactDOMServer from 'react-dom/server';
import BeaconChartWithLegend from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import { BeaconSubtitleContainer } from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconSubtitleContainer';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import HoverTooltip from 'src/components/BeaconV2Components/HoverTooltip/HoverTooltip';
import SplineChartLoading from 'src/components/BeaconV2Components/SplineChartLoading/SplineChartLoading';
import { GenericXAxisLabel } from 'src/components/BeaconV2Components/TopEntityChartV2/GenericXAxisLabel';
import TopEntityChartV2 from 'src/components/BeaconV2Components/TopEntityChartV2/TopEntityChartV2';
import {
  useEntityPhrasing,
  useTaxonomySubtitleText
} from 'src/components/BeaconV2Components/common/BeaconProDynamicSubtitle/hooks';
import SubtitleFilters from 'src/components/Charts/SubtitleFilters';
import useAdvancedSearchData, { AdvancedSearchParsedDatum } from 'src/serverProxy/useAdvancedSearchData';

import {
  useAppSelector,
  useComparisonTimePeriodShortDisplayName,
  useMainTimePeriodShortDisplayName
} from 'src/utils/Hooks';
import { calculatePercentChange, formatMetric } from 'src/utils/app';
import { saveBarChartAsDelimitedData } from 'src/utils/chartUtils';
import { getYearForWeekId } from 'src/utils/dateUtils';
import { getWeekLastDay } from 'src/utils/dateformatting';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { BEACON_INDEX_NAMES } from 'src/utils/entityDefinitions/indexes/beaconIndexNames';
import { StacklineDate } from 'stackline-datetime';

const parseData = (data: AdvancedSearchParsedDatum[]) => {
  const getWeekIdWithoutYear = (weekId) => String(weekId).substring(4);

  const [volumeData, compareData] = data.map((d) => d?.data[0]?.aggregations?.by_weekId || []);

  const sortByWeekId = (arr) => arr.sort((a, b) => a.weekId - b.weekId);

  const sortedVolumeData = sortByWeekId(volumeData);
  const sortedCompareData = sortByWeekId(compareData);

  const fillFullYearWeekIds = (datum) => {
    const full52WeeksWeekIds = [];
    let currentLastWeekId = datum.length ? datum[0].weekId : 101;
    let index = 0;
    while (full52WeeksWeekIds.length < 52) {
      const currentWeek = datum[index];
      let year: number, week: number;
      try {
        year = getYearForWeekId(currentLastWeekId);
        week = parseInt(getWeekIdWithoutYear(currentLastWeekId), 10);
      } catch (e) {
        // if the week go to 53 on a non-leap year, stop the loop
        break;
      }

      if (week > 52) {
        try {
          // check if this year is a leap year
          StacklineDate.getStartDateSDS({ year, week });
        } catch (e) {
          // this is a non-leap year, so we are sliding to the next year
          year += 1;
          week = 1;
        }
      }

      full52WeeksWeekIds.push({
        fieldName: 'weekId',
        fieldId: currentWeek ? currentWeek.weekId : `${year}${String(week).padStart(2, '0')}`,
        weekId: currentWeek ? currentWeek.weekId : `${year}${String(week).padStart(2, '0')}`,
        value: currentWeek ? currentWeek.value : 'INVALID'
      });

      week += 1;
      currentLastWeekId = `${year}${String(week).padStart(2, '0')}`;
      index += 1;
    }

    return full52WeeksWeekIds;
  };

  const mapDataToWeeks = (weekData) => weekData.map((d) => [getWeekLastDay(d.weekId), d.value]);

  const currentData = mapDataToWeeks(fillFullYearWeekIds(sortedVolumeData));
  const comparisonData = mapDataToWeeks(fillFullYearWeekIds(sortedCompareData));

  const createDiffMap = (mainDataSet, compareDataSet) => {
    const diffMap = new Map();

    mainDataSet.forEach((mainDatum) => {
      const weekIdWithoutYear = getWeekIdWithoutYear(mainDatum.weekId);
      const compareDatum = compareDataSet.find((d) => getWeekIdWithoutYear(d.weekId) === weekIdWithoutYear) || {
        value: null
      };

      diffMap.set(getWeekLastDay(mainDatum.weekId), calculatePercentChange(mainDatum.value, compareDatum.value));
    });

    return diffMap;
  };

  const diffMapForMain = createDiffMap(sortedVolumeData, sortedCompareData);
  const diffMapForComp = createDiffMap(sortedCompareData, sortedVolumeData);

  return {
    compareData: comparisonData,
    currentData,
    diffMapForMain,
    diffMapForComp,
    exportCurrentData: mapDataToWeeks(sortedVolumeData),
    exportCompareData: mapDataToWeeks(sortedCompareData)
  };
};

const YAxisLabel = ({ value }: { value: string }) => {
  return (
    <div>
      {formatMetric(value, METRICTYPE.INTEGER, {
        showFullValue: false
      })}
    </div>
  );
};

/**
 * Weekly Volume chart grouped by weekId.
 *
 */
const WeeklyReviewProductChart = () => {
  const comparisonTimePeriodShortDisplayName = useComparisonTimePeriodShortDisplayName();
  // This is a special graph that shows only one graph at a time
  const [chartType, setChartType] = useState<'primary' | 'comparison'>('primary');
  const mainTimePeriodShortDisplayName = useMainTimePeriodShortDisplayName();
  const entityPhrase = useEntityPhrasing();
  const taxonomyText = useTaxonomySubtitleText();
  const [isHoverMainLegend, setIsHoverMainLegend] = useState(false);
  const [isHoverCompareLegend, setIsHoverCompareLegend] = useState(false);
  const legendLabel = useAppSelector((state) => state.mainTimePeriod.displayName);
  const legendComparisonLabel = useAppSelector((state) => state.comparisonTimePeriod.displayName);
  const theme = useStacklineTheme();

  const YAxisTextStyles: Highcharts.CSSObject = {
    fontFamily: theme.text.fontFamily,
    color: theme.colors.primary,
    fontSize: '12px'
  };

  const { data, isLoading } = useAdvancedSearchData({
    fields: [{ name: 'weeklyReviewVolume' }],
    indexName: `${BEACON_INDEX_NAMES.RATINGS_REVIEWS_METRICS_V2}`,
    groupByField: 'weekId',
    requestId: 'weekly-review-volume-columnChart',
    shouldFetchComparison: true,
    returnRawResponse: true
  });

  const { compareData, currentData, diffMapForMain, diffMapForComp, exportCurrentData, exportCompareData } = !isLoading
    ? parseData(data)
    : {
        compareData: [],
        currentData: [],
        diffMapForMain: new Map(),
        diffMapForComp: new Map(),
        exportCompareData: [],
        exportCurrentData: []
      };

  const dataToUse = useMemo(() => {
    if (chartType === 'comparison') {
      return compareData;
    }
    return currentData;
  }, [compareData, currentData]);

  // This is used for YAxis - sometimes YAxis can be just 1, or 2, when design mock has 5 minimum
  const maxValue = useMemo(() => {
    let currentMax = 0;
    dataToUse.forEach(([, value]: [unknown, string | number]) => {
      if (typeof value === 'number') {
        currentMax = Math.max(currentMax, value);
      }
    });
    return currentMax;
  }, [dataToUse]);

  return (
    <BeaconChartWithLegend
      title="Weekly Review Volume"
      includeSummaryInfo={false}
      subtitleSX={{
        marginTop: '8px'
      }}
      convertSeriesToDelimitedData={async () => {
        return saveBarChartAsDelimitedData(exportCurrentData, exportCompareData, {
          metricLabel: 'Weekly Review Volume'
        });
      }}
      primaryLegendProps={{
        displayName: legendLabel,
        legendIndicatorProps: {
          legendOnClick: () => setChartType('primary'),
          ...(chartType === 'comparison' ? { color: 'casper', textColor: 'casper', opacity: 0.4 } : {}),
          legendOnHover: (event) => {
            const isHover = event.type === 'mouseenter';
            setIsHoverMainLegend(isHover);
          },
          ...(chartType === 'comparison' && !isHoverMainLegend
            ? { color: 'casper', textColor: 'casper', opacity: 0.4 }
            : {})
        }
      }}
      comparisonLegendProps={{
        displayName: legendComparisonLabel,
        color: 'secondary',
        legendIndicatorProps: {
          legendOnClick: () => setChartType('comparison'),
          legendOnHover: (event) => {
            const isHover = event.type === 'mouseenter';
            setIsHoverCompareLegend(isHover);
          },

          ...(chartType === 'primary' && !isHoverCompareLegend
            ? { color: 'casper', textColor: 'casper', opacity: 0.4 }
            : {})
        }
      }}
      subtitle={
        <BeaconSubtitleContainer>
          <Text variant="body1">
            {`The chart displays the review volume for ${entityPhrase} `}
            {taxonomyText}{' '}
            {`during the
            ${mainTimePeriodShortDisplayName} period compared to the ${comparisonTimePeriodShortDisplayName}`}
            .
            <SubtitleFilters />
          </Text>
        </BeaconSubtitleContainer>
      }
      loading={isLoading}
      loadingComponent={<SplineChartLoading width={1000} />}
    >
      <TopEntityChartV2
        reverseAxisStartPosition
        useStaticCategory
        maxDataPointsToShow={51}
        leftAlignBars
        xAxisFormatter={(xAxisCategory) => {
          return ReactDOMServer.renderToStaticMarkup(<GenericXAxisLabel xAxisCategory={xAxisCategory} />);
        }}
        seriesColors={chartType === 'primary' ? [theme.colors.primary] : [theme.colors.casper, theme.colors.primary]}
        chartSeries={[
          {
            type: 'column',
            data: dataToUse,
            color: theme.colors.primary,
            metricType: METRICTYPE.VOLUME,
            categories: [{ type: 'weekId' }]
          }
        ]}
        chartOptionsOverride={(option) => {
          const clonedOption = { ...option };
          clonedOption.chart.marginTop = 10;
          clonedOption.xAxis[0].max = 51;
          clonedOption.xAxis[0].labels.step = 5;
          clonedOption.yAxis = Array.from({ length: 1 }).map(() => ({
            gridLineWidth: 0,
            title: {
              text: ''
            },
            offset: 13,
            labels: {
              formatter() {
                const { value } = this;
                return ReactDOMServer.renderToStaticMarkup(<YAxisLabel value={value} />);
              },
              style: YAxisTextStyles
            },
            tickAmount: Math.min(5, maxValue)
          }));
          clonedOption.series[0].pointWidth = 8;
          clonedOption.series[0].pointPadding = 0;
          clonedOption.series[0].groupPadding = 0;
          clonedOption.series[0].enableMouseTracking = true;
          clonedOption.plotOptions.series.dataLabels.enabled = false;
          clonedOption.tooltip = {
            enabled: true,
            shared: true,
            shadow: false,
            hideDelay: 0,
            outside: true,
            backgroundColor: 'transparent',
            borderColor: 'transparent',
            useHTML: true,
            formatter() {
              const mapToUse = chartType === 'primary' ? diffMapForMain : diffMapForComp;
              const percentChange = +mapToUse.get(this.x.name);

              return ReactDOMServer.renderToStaticMarkup(
                <HoverTooltip
                  theme={theme}
                  title={this.points[0]?.key as unknown as string}
                  primaryColor={theme.colors.primary}
                  primaryMetric={`${formatMetric(this.y, METRICTYPE.VOLUME, {
                    decimalPlaces: 2
                  })}`}
                  change={
                    percentChange !== undefined
                      ? `${formatMetric(percentChange, METRICTYPE.PERCENT, { decimalPlaces: 2 })}`
                      : undefined
                  }
                  rawChange={percentChange}
                />
              );
            }
          };

          return clonedOption;
        }}
      />
    </BeaconChartWithLegend>
  );
};

export default WeeklyReviewProductChart;
